const { registerBlockType } = wp.blocks;
const { PanelBody, SelectControl, ToggleControl } = wp.components;
const { InspectorControls, RichText } = wp.blockEditor;
const { useEffect, useState, useRef } = wp.element;
const { apiFetch } = wp;
const { useSelect } = wp.data;
const { __ } = wp.i18n;

registerBlockType('pg/posts-list-with-filters', {
  title: 'PG Posts List with Filters',
  icon: 'megaphone',
  category: 'widgets',
  attributes: {
    headingText: {
      type: 'string',
      default: 'Browse our courses',
    },
    sectionClass: {
      type: 'string',
      default: 'section-light-secondary',
    },
    cornerPositionClass: {
      type: 'string',
      default: 'corner-left-top',
    },
    cornerBackgroundClass: {
      type: 'string',
      default: 'corner-on-light-primary',
    },
    selectedPostType: {
      type: 'string',
      default: 'posts',
    },
    showExcerpt: {
      type: 'boolean',
      default: true,
    },
    perPage: {
      type: 'number',
      default: 3,
    },
  },
  edit: ({ attributes, setAttributes, isSelected }) => {
    const {
      headingText,
      sectionClass,
      cornerPositionClass,
      cornerBackgroundClass,
      selectedPostType,
      showExcerpt,
      perPage,
    } = attributes;
    const [postTypes, setPostTypes] = useState([
      { value: 'posts', label: 'Posts' },
      { value: 'course', label: 'Courses' },
    ]);
    const [categories, setCategories] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [filteredPosts, setFilteredPosts] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMorePosts, setHasMorePosts] = useState(true);
    const lastLoadedItemRef = useRef(null);

    const darkMode = sectionClass.includes('light');

    useEffect(() => {
      // Fetch categories for the selected post type
      const taxonomy =
        selectedPostType === 'course' ? 'course_category' : 'categories';
      apiFetch({
        path: `/wp/v2/${taxonomy}`,
      }).then((cats) => {
        const categoriesArray = cats.map((cat) => ({
          value: cat.id,
          label: cat.name,
        }));
        setCategories(categoriesArray);
      });
    }, [selectedPostType]);

    useEffect(() => {
      // Fetch filtered posts based on selected categories, post type, and current page
      if (selectedCategories.length > 0) {
        const categoriesString = selectedCategories.join(',');
        apiFetch({
          path: `/wp/v2/${selectedPostType}?categories=${categoriesString}&_embed&per_page=${perPage}&page=${currentPage}`,
        })
          .then((posts) => {
            if (posts.length < perPage) {
              setHasMorePosts(false);
            } else {
              setHasMorePosts(true);
            }
            setFilteredPosts((prevPosts) =>
              currentPage === 1 ? posts : [...prevPosts, ...posts]
            );
          })
          .catch((error) => {
            console.error('Error fetching posts:', error);
            setFilteredPosts([]);
          });
      } else {
        // Fetch all posts of the selected post type if no categories are selected
        apiFetch({
          path: `/wp/v2/${selectedPostType}?_embed&per_page=${perPage}&page=${currentPage}`,
        })
          .then((posts) => {
            if (posts.length < perPage) {
              setHasMorePosts(false);
            } else {
              setHasMorePosts(true);
            }
            setFilteredPosts((prevPosts) =>
              currentPage === 1 ? posts : [...prevPosts, ...posts]
            );
          })
          .catch((error) => {
            console.error('Error fetching posts:', error);
            setFilteredPosts([]);
          });
      }
    }, [selectedCategories, selectedPostType, currentPage]);

    const onChangeSectionClass = (newClass) => {
      setAttributes({ sectionClass: newClass });
    };

    const onChangeCornerPositionClass = (newClass) => {
      setAttributes({ cornerPositionClass: newClass });
    };

    const onChangeCornerBackgroundClass = (newClass) => {
      setAttributes({ cornerBackgroundClass: newClass });
    };

    const onPostTypeChange = (newPostType) => {
      setAttributes({ selectedPostType: newPostType });
      setSelectedCategories([]);
      setCurrentPage(1);
    };

    const onLoadMore = () => {
      setCurrentPage((prevPage) => prevPage + 1);
    };

    useEffect(() => {
      if (lastLoadedItemRef.current) {
        lastLoadedItemRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, [filteredPosts]);

    return (
      <div>
        <InspectorControls>
          <PanelBody title={__('Settings')}>
            <SelectControl
              label={__('Section Class')}
              value={sectionClass}
              options={[
                { label: 'Light Primary', value: 'section-light-primary' },
                { label: 'Dark Primary', value: 'section-dark-primary' },
                { label: 'Light Secondary', value: 'section-light-secondary' },
                { label: 'Dark Secondary', value: 'section-dark-secondary' },
              ]}
              onChange={onChangeSectionClass}
            />
            <SelectControl
              label={__('Corner Position')}
              value={cornerPositionClass}
              options={[
                { label: 'Left-Top', value: 'corner-left-top' },
                { label: 'Left-Bottom', value: 'corner-left-bottom' },
                { label: 'Right-Top', value: 'corner-right-top' },
                { label: 'Right-Bottom', value: 'corner-right-bottom' },
              ]}
              onChange={onChangeCornerPositionClass}
            />
            <SelectControl
              label={__('Corner Background')}
              value={cornerBackgroundClass}
              options={[
                { label: 'On Light Primary', value: 'corner-on-light-primary' },
                {
                  label: 'On Light Secondary',
                  value: 'corner-on-light-secondary',
                },
                { label: 'On Dark Primary', value: 'corner-on-dark-primary' },
                {
                  label: 'On Dark Secondary',
                  value: 'corner-on-dark-secondary',
                },
              ]}
              onChange={onChangeCornerBackgroundClass}
            />
            <SelectControl
              label={__('Post Type')}
              value={selectedPostType}
              options={postTypes}
              onChange={onPostTypeChange}
            />
            <SelectControl
              label={__('Per Page')}
              value={perPage}
              options={[
                { label: '3', value: 3 },
                { label: '6', value: 6 },
                { label: '9', value: 9 },
                { label: '12', value: 12 },
                { label: '15', value: 15 },
                { label: '18', value: 18 },
                { label: '21', value: 21 },
                { label: '24', value: 24 },
              ]}
              onChange={(newPerPage) => {
                setAttributes({ perPage: newPerPage });
                setCurrentPage(1);
              }}
            />
            <ToggleControl
              label={__('Show Excerpt')}
              checked={showExcerpt}
              onChange={() => setAttributes({ showExcerpt: !showExcerpt })}
            />
          </PanelBody>
        </InspectorControls>
        <section className={sectionClass}>
          <div
            className={`corner ${cornerPositionClass} ${sectionClass.replace(
              'section',
              'corner'
            )} ${cornerBackgroundClass}`}
          ></div>
          <div className="courses-section-wrapper">
            <div className="content-container">
              <RichText
                tagName="h2"
                value={headingText}
                onChange={(newText) => setAttributes({ headingText: newText })}
                placeholder={__('Enter heading...')}
              />
              <div className="filters">
                <div
                  className={`btn ${darkMode ? 'btn-dark' : 'btn-light'} btn-outline btn-small filter ${
                    selectedCategories.length === 0 ? 'active' : ''
                  }`}
                  onClick={() => {
                    setSelectedCategories([]);
                    setCurrentPage(1);
                  }}
                >
                  All items
                </div>
                {categories.map((cat) => (
                  <div
                    key={cat.value}
                    className={`btn ${darkMode ? 'btn-dark' : 'btn-light'} btn-outline btn-small filter ${
                      selectedCategories.includes(cat.value) ? 'active' : ''
                    }`}
                    onClick={() => {
                      setSelectedCategories((prev) =>
                        prev.includes(cat.value)
                          ? prev.filter((c) => c !== cat.value)
                          : [...prev, cat.value]
                      );
                      setCurrentPage(1);
                    }}
                  >
                    {cat.label}
                  </div>
                ))}
              </div>
              <div className="courses-list">
                {filteredPosts.map((post, index) => (
                  <div
                    key={post.id}
                    className="course-card"
                    ref={
                      index === filteredPosts.length - 1
                        ? lastLoadedItemRef
                        : null
                    }
                  >
                    <div className="title">
                      <div className="top">
                        {post._embedded &&
                          post._embedded['wp:term'] &&
                          post._embedded['wp:term'][0] &&
                          post._embedded['wp:term'][0][0] &&
                          post._embedded['wp:term'][0][0].name && (
                            <div className="label">
                              {post._embedded['wp:term'][0][0].name}
                            </div>
                          )}
                        <a
                          href={post.link}
                          className="btn btn-dark btn-outline btn-icon"
                        ></a>
                      </div>
                      <h3>{post.title.rendered}</h3>
                    </div>
                    {showExcerpt && post.excerpt && post.excerpt.rendered && (
                      <p
                        className="description"
                        dangerouslySetInnerHTML={{
                          __html: post.excerpt.rendered,
                        }}
                      ></p>
                    )}
                  </div>
                ))}
              </div>
              {hasMorePosts && (
                <div className="load-more-wrapper">
                  <button
                    className={`btn ${darkMode ? 'btn-dark' : 'btn-light'} btn-solid`}
                    onClick={onLoadMore}
                  >
                    Load More
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    );
  },
  save: () => null,
});
